import React from "react";

import {
  Code,
  HelpIcon,
  LoadingIcon as Loading,
  Typography
} from "@packages/ds";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import styled from "styled-components";

import ListGroup from "Components/ListGroup";
import PageDescription from "Components/PageDescription";
import PageMeta from "Components/PageMeta";
import { APP_NAME } from "Constants/documentationUrls";
import { Link } from "ds/Button";
import SettingLine from "ds/SettingLine";
import {
  gitProjectSelector,
  getIsLoadingProjects
} from "Reducers/project/project";
import { useAppSelector } from "Store/hooks";

const Layout = styled.div`
  word-break: break-all;
  word-wrap: break-word;
  width: 100%;
`;

const ProjectDeployKeyLayout = styled.div`
  word-break: break-all;
  word-wrap: break-word;
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
`;

const Help = styled.p`
  margin: 16px 0 24px 0;
  line-height: 24px;
  color: var(--section-ebony-light-color, var(--ebony-light));
  font-size: 14px;
  svg {
    margin: 0 8px 0 0;
    vertical-align: middle;
  }
  span {
    text-transform: lowercase;
  }
`;

const Title = styled(Typography.Title)`
  margin-bottom: 11px;
  &::first-letter {
    text-transform: capitalize;
  }
`;

const LoadingWrapper = styled.div`
  display: block;
  padding-top: 50px;
`;

function ProjectDeployKey({ projectId, organizationId }) {
  const intl = useIntl();
  const project = useAppSelector(state =>
    gitProjectSelector(state, {
      organizationId: organizationId,
      projectId: projectId
    })
  );
  const clientSshKey = project?.repository?.client_ssh_key;
  const isLoading = useAppSelector(getIsLoadingProjects);

  return (
    <Layout>
      <PageMeta title={`Deploy key | ${project?.title}`} />
      <Title tag="h2" id="settings-heading">
        {intl.formatMessage({ id: "deploy_key" })}
      </Title>
      <PageDescription>
        {intl.formatMessage(
          { id: "deploy_key_description" },
          { appName: APP_NAME }
        )}
      </PageDescription>
      <Help>
        <HelpIcon />
        {intl.formatMessage({ id: "settings.deployKey.help.a" })}{" "}
        <Link
          analyticId="settings.deployKey.help.b"
          href={intl.formatMessage({ id: "links.documentation.deploy_key" })}
        >
          <span>{intl.formatMessage({ id: "settings.deployKey.help.b" })}</span>
        </Link>{" "}
      </Help>

      <ListGroup aria-labelledby="settings-heading">
        {isLoading && (
          <LoadingWrapper>
            <Loading />
          </LoadingWrapper>
        )}
        <SettingLine
          disableBoxShadow
          info={
            <span className="deploy-key-field">
              {intl.formatMessage({ id: "ssh_deploy_key" })}
            </span>
          }
          noToggle={true}
          isOpen={true}
        >
          <ProjectDeployKeyLayout>
            <Code.Block
              singleLine={false}
              separateCopyButton
              id="project-deploy-key"
            >
              {clientSshKey}
            </Code.Block>
          </ProjectDeployKeyLayout>
        </SettingLine>
      </ListGroup>
    </Layout>
  );
}

ProjectDeployKey.propTypes = {
  clientSshKey: PropTypes.string,
  isLoading: PropTypes.bool,
  projectId: PropTypes.string,
  project: PropTypes.object,
  organizationId: PropTypes.string
};

export default ProjectDeployKey;
